import { Component, computed, input, ViewEncapsulation } from '@angular/core'
import { ZoneId } from '@js-joda/core'
import { DatePipe } from '../../pipes'
import { DateFormat, JodaConvertible } from '../../util'

@Component({
  selector: 'ftr-date',
  templateUrl: './date.component.html',
  styleUrls: ['./date.component.css'],
  encapsulation: ViewEncapsulation.None,
  imports: [DatePipe],
})
/**
 * Displays a date in a given format, optionally showing a tooltip which when hovered
 * shows an extended date format.
 *
 * @see DatePipe
 *
 * Note: the tooltip on this component is displayed by using an HTML title="" attribute. This means that
 * it does not work no mobile (since there is no hover on mobile) and is hidden on some browsers
 * when the date text is clicked. If we find that the tooltip has been useful for people, I'd recommend that
 * we look at using a JS/CSS method of displaying the tooltip, which would work the same for all our users.
 */
export class DateComponent {
  /**
   * The date to display
   */
  date = input.required<JodaConvertible | undefined | null>()
  /**
   * The format to display the date in
   */
  format = input.required<DateFormat>()
  /**
   * The format to display the date in, inside the tooltip.
   * If undefined, no tooltip is shown.
   */
  tooltipFormat = input<DateFormat | undefined>()
  /**
   * The value to display if the date is falsy or can't be converted (defaults to empty string)
   */
  defaultValue = input<string>('')
  /**
   * The timezone to use to convert the value to for display.
   * Only applies to ZonedDateTime values or strings parsed as ZonedDateTime.
   */
  timeZoneId = input<ZoneId | string>()
  /**
   * If set to true, the value will be displayed in the system's timezone.
   * Will override the `timeZoneId` input if set.
   */
  useSystemTimeZone = input<boolean>(false)

  timeZone = computed(() => (this.useSystemTimeZone() ? ZoneId.systemDefault() : this.timeZoneId()))
}
